/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Content Helper utilities for MathJax and Markdown processing
 *
 * @file mathJaxLoader.js
 * @author Murhsid Ahamedh
 */

// eslint-disable-next-line import/prefer-default-export
export const loadMathJax = () => new Promise((resolve, reject) => {
  // Check if MathJax 3.2.2 is already available
  if (window.MathJax && window.MathJax.version && window.MathJax.version === '3.2.2') {
    resolve();

    return;
  }

  // Check if script is already loading
  if (document.querySelector('script[src*="mathjax"][src*="tex-mml-chtml"]')) {
    // Wait for existing script to load
    const checkInterval = setInterval(() => {
      if (window.MathJax && window.MathJax.version && window.MathJax.version === '3.2.2') {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.MathJax || !window.MathJax.version || window.MathJax.version !== '3.2.2') {
        reject(new Error('MathJax loading timeout'));
      }
    }, 10000);

    return;
  }

  // Configure MathJax before loading
  window.MathJax = {
    loader: {
      load: ['[tex]/mhchem', '[tex]/mathtools', '[tex]/noerrors', 'input/tex', 'input/asciimath', 'output/chtml']
    },
    startup: {
      ready() {
        const mathJax = window.MathJax;
        if (mathJax && mathJax._ && mathJax._.handlers && mathJax._.handlers.html) {
          const { HTMLDocument } = mathJax._.handlers.html.HTMLDocument;
          HTMLDocument.prototype.findPosition = function (N, index, delim, nodes) {
            const adaptor = this.adaptor;
            const inc = 1 / (nodes[N].length || 1);
            let i = N;
            // eslint-disable-next-line no-restricted-syntax
            for (const [node, n] of nodes[N]) {
              if (index <= n && adaptor.kind(node) === '#text') {
                return { i, node, n: Math.max(index, 0), delim };
              }
              // eslint-disable-next-line no-param-reassign
              index -= n;
              i += inc;
            }

            return { node: null, n: 0, delim };
          };
        }
        if (mathJax && mathJax.startup && mathJax.startup.defaultReady) {
          mathJax.startup.defaultReady();
        }
      }
    },
    asciimath: { delimiters: [['\\$$', '\\$$']] },
    tex: {
      inlineMath: [['\\(', '\\)'], ['\\[', '\\]'], ['$', '$']],
      displayMath: [['$$', '$$']],
      packages: { '[+]': ['mhchem', 'mathtools'] },
      macros: { prescript: ['{}^{#1}_{#2}', 2] }
    }
  };

  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.min.js';
  scriptElement.async = true;

  scriptElement.onload = () => {
    // Verify MathJax loaded correctly
    if (window.MathJax && window.MathJax.version && window.MathJax.version === '3.2.2') {
      resolve();
    } else {
      reject(new Error('MathJax loaded but version mismatch'));
    }
  };

  scriptElement.onerror = () => {
    reject(new Error('Failed to load MathJax from CDN'));
  };

  document.head.appendChild(scriptElement);
});
